import React from 'react';
import { Cake, Crown, Gift, Heart, MapPin, Music, Play, Sparkles, Star } from 'lucide-react';

const themeStyles = {
  classic: {
    screen: 'from-slate-900 via-slate-950 to-amber-950/50',
    accent: 'text-amber-400',
    icon: Heart
  },
  interactive: {
    screen: 'from-slate-900 via-indigo-950/80 to-violet-950/40',
    accent: 'text-violet-400',
    icon: Gift
  },
  animated: {
    screen: 'from-slate-900 via-sky-950/60 to-indigo-950/40',
    accent: 'text-sky-400',
    icon: Sparkles
  },
  superAnimated: {
    screen: 'from-slate-900 via-fuchsia-950/40 to-amber-950/50',
    accent: 'text-fuchsia-400',
    icon: Star
  },
  website: {
    screen: 'from-slate-900 via-stone-950 to-amber-950/40',
    accent: 'text-amber-400',
    icon: Crown
  },
  kids: {
    screen: 'from-slate-900 via-emerald-950/40 to-amber-950/30',
    accent: 'text-emerald-400',
    icon: Cake
  },
  sweet15: {
    screen: 'from-slate-900 via-rose-950/30 to-amber-950/50',
    accent: 'text-amber-300',
    icon: Crown
  },
  wedding: {
    screen: 'from-slate-900 via-orange-950/25 to-amber-950/45',
    accent: 'text-orange-300',
    icon: Heart
  },
  babyReveal: {
    screen: 'from-slate-900 via-sky-950/35 to-rose-950/30',
    accent: 'text-sky-300',
    icon: Gift
  }
};

export function DigitalInvitesPhoneMockup({ theme = 'classic', themeLabel, showPlay = false, showMusic = false }) {
  const style = themeStyles[theme] || themeStyles.classic;
  const Icon = style.icon;

  return (
    <div className="relative mx-auto w-full max-w-[220px] sm:max-w-[240px]">
      <div
        className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-amber-500/15 via-transparent to-violet-500/10 blur-xl pointer-events-none"
        aria-hidden
      />
      <div className="relative rounded-[2rem] border border-white/10 bg-slate-950/90 p-2 shadow-[0_30px_80px_-24px_rgba(0,0,0,0.55)] backdrop-blur-md">
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-1.5 rounded-full bg-black/80 z-10" aria-hidden />
        <div className={`relative overflow-hidden rounded-[1.35rem] aspect-[9/16] bg-gradient-to-b ${style.screen}`}>
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_50%_15%,rgba(245,158,11,0.12),transparent_55%)] pointer-events-none" aria-hidden />
          <div className="relative h-full flex flex-col items-center justify-center p-4 text-center">
            <div
              className={`w-12 h-12 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center mb-3 ${style.accent}`}
            >
              <Icon className="w-6 h-6" aria-hidden />
            </div>
            {themeLabel ? (
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/45 mb-2 px-2 leading-snug">
                {themeLabel}
              </p>
            ) : null}
            <div className="h-2 w-20 rounded-full bg-white/10 mb-1.5" aria-hidden />
            <div className="h-1.5 w-14 rounded-full bg-white/[0.06] mb-3" aria-hidden />
            {showPlay ? (
              <div className="w-11 h-11 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-2">
                <Play className="w-5 h-5 text-amber-300 ml-0.5" aria-hidden />
              </div>
            ) : null}
            {showMusic ? (
              <div className="flex items-center gap-1.5 rounded-full bg-white/[0.06] px-2.5 py-1 border border-white/10">
                <Music className="w-3 h-3 text-emerald-400" aria-hidden />
                <div className="flex gap-0.5 items-end h-3">
                  {[2, 4, 3, 5, 2].map((h, i) => (
                    <div key={i} className="w-0.5 rounded-full bg-emerald-400/50" style={{ height: `${h * 2}px` }} />
                  ))}
                </div>
              </div>
            ) : null}
            <div className="absolute bottom-3 left-3 right-3 flex justify-center gap-2">
              <MapPin className="w-3 h-3 text-white/25" aria-hidden />
              <Sparkles className="w-3 h-3 text-amber-400/40" aria-hidden />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
